// Global CSS surface mirrors the legacy Angular shell (angular.json loaded
// bootstrap.min.css first, then raptor-shell-ui styles which @import CLD's
// `_shadow-dom`). Order matters: Bootstrap first so motif-theme + the CLD/Kendo
// themes override it. MFEs rely on Bootstrap grid/utility classes (.row/.col,
// .d-none/.collapse) for layout and visibility.
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/motif-theme.scss';
import './styles/legacy-cld-themes.css';
import './styles/legacy-cld-modal.css';

import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from '@/http/query-client';
import { initializeMsal, msalInstance } from '@/auth/msal';
import { initializeAuth0 } from '@/auth/auth0';
import { useClientStore } from '@/client/client-store';
import { warmUpAdditionalInstances } from '@/auth/multi-client-token';
import { registerMotifComponents, restoreTheme } from '@/motif/setup';
import { registerShellSdk } from '@/sdk/register';
import { flushTelemetry, initializeTracker } from '@/telemetry/tracker';
import { installWindowTelemetry } from '@/telemetry/window-telemetry';
import { installCrossTabClientRefresh } from '@/client/cross-tab';
import { installLegacyGlobals } from '@/legacy/install-globals';
import { installLocalizePolyfill } from '@/legacy/install-localize-polyfill';
import { installIframeGlobalsBridge } from '@/mfe/iframe-globals-bridge';
import { installChatbotMessageListener } from '@/mfe/chatbot-message-bridge';
import {
  initiatePortalSignalR,
  closeConnection as closeSignalRConnection,
} from '@/legacy/signal-r';
import { onLogout } from '@/auth/auth-module';
import { startIncidentStatus } from '@/incidents/incidents-bridge';
import { startPlatformNotifications } from '@/platform-notifications/platform-notifications-bridge';
import { startPortalMessageDispatch } from '@/legacy/portal-message-dispatch';
import { startLegacyUserBridge } from '@/legacy/legacy-user-bridge';
import { installHeaderBridge } from '@/header/header-bridge';
import { setMcpHostToken } from '@/mfe/mcp-host-token';
import { AppRouter } from '@/router';

async function bootstrap() {
  // Install the `$localize` pass-through polyfill first — Angular MFEs compiled
  // with i18n reference the global `$localize` and throw on load without it.
  installLocalizePolyfill();
  // Legacy `app.component` ngOnInit parity: clear the MFE-loaded marker on shell
  // init so a duplicated tab / page refresh preloads the MFE again.
  try {
    sessionStorage.removeItem('IsMfeLoadedAlready');
  } catch {
    /* sessionStorage unavailable */
  }
  restoreTheme();
  registerMotifComponents();
  initializeTracker();
  // Global uncaught-error / unhandled-rejection / browser-close telemetry
  // (legacy Angular global ErrorHandler + beforeunload BROWSERCLOSE parity).
  installWindowTelemetry();
  await initializeMsal();
  // Construct the Auth0 client when configured (no-op otherwise). This only
  // builds the client — no iframe/redirect fires at boot, so MSAL-only users
  // are unaffected. Session establishment happens in the route-scoped
  // `ensureAuth0ForMfe` flow and the Auth0 callback route.
  await initializeAuth0();
  registerShellSdk();
  installLegacyGlobals();
  // Bridge those globals into same-origin MFE iframes (e.g. USIR) so an
  // iframe-mounted legacy app can read clientId / token / user the same way an
  // inline-mounted one does. Must run after installLegacyGlobals().
  installIframeGlobalsBridge();
  installChatbotMessageListener();
  installHeaderBridge();
  startLegacyUserBridge();
  installCrossTabClientRefresh();

  const container = document.getElementById('shell-root');
  if (!container) throw new Error('Root container #shell-root not found');
  // NOTE: no <StrictMode>. Its dev-only double-mount cycle re-initialises
  // every Motif Stencil web component twice, which leaves orphaned context
  // subscribers (e.g. motif-listbox stops reacting to motif-base-select's
  // `isOpen` updates — dropdown clicks register but the panel never opens).
  createRoot(container).render(
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </MsalProvider>,
  );

  // Post-login services (SignalR, incident banner, portal/RUM dispatch) must
  // start ONLY after authentication completes. Legacy parity: the Angular shell
  // starts these inside `app-container.component` — which mounts behind
  // MsalGuard — never in parallel with login. Firing them before an MSAL active
  // account exists races the AAD redirect and fails the SignalR negotiate with
  // a 401 (no bearer token yet).
  startPostLoginServicesWhenReady();
}

let postLoginServicesStarted = false;

/**
 * Start the authenticated-only background services exactly once, enforcing the
 * login → client → services sequence:
 *   - SignalR real-time channel (`initiatePortalSignalR`)
 *   - incident banner subscription + initial fetch (`startIncidentStatus`)
 *   - portal/RUM message dispatch (`startPortalMessageDispatch`)
 *
 * SignalR `Negotiatesignalr` and the incident-status fetch both hit
 * RUM-protected APIM endpoints (`role-scr-mgmt/api`, `incidentstatus`) that
 * require a `Raptor-Authorization` RUM token keyed by the ACTIVE clientId. That
 * clientId only exists once the client list has resolved (`useClients` →
 * `setCurrent`), so these must not fire at bootstrap — see
 * {@link startPostLoginServicesWhenReady}. Legacy starts them from
 * `AppContainerComponent`, which mounts only after client context resolves.
 */
function startPostLoginServices(): void {
  if (postLoginServicesStarted) return;
  postLoginServicesStarted = true;

  // This gate is exactly "signed in with a resolved client", which is when the
  // logger's pre-auth buffer becomes safe to release: the records finally get
  // the user and client dimensions instead of placeholders.
  flushTelemetry();

  // Reached only once an active client is resolved (see
  // startPostLoginServicesWhenReady), so the RUM token these calls need is
  // available and SignalR negotiate / incident fetch no longer 401.
  void initiatePortalSignalR();
  // Close the hub explicitly on logout (legacy AppContainer ngOnDestroy).
  onLogout(closeSignalRConnection);
  startIncidentStatus();
  // Live platform-notification toasts over the SignalR `portalnotificationmethod` hub.
  startPlatformNotifications();
  startPortalMessageDispatch();
  // Warm the additional app-registration MSAL instances (enableSingleTenantAuth)
  // so the first MFE token request that needs a non-primary registration
  // resolves silently instead of paying an SSO round-trip mid-request.
  void warmUpAdditionalInstances();
  // Legacy `sendTokenForMCPClient` parity: relay the Transactions Concierge
  // token to the MCP chat host for allowlisted clients. No-op unless the flag is
  // on (QA-only) and the user holds an allowlisted client.
  void setMcpHostToken();
}

/**
 * Gate {@link startPostLoginServices} on BOTH an MSAL active account AND a
 * resolved active client. The active client is what supplies the RUM token the
 * RUM-protected post-login calls need; firing before it resolves 401s the
 * SignalR negotiate (`negotiate failed : HttpError: 401 Unauthorized`) and the
 * incident fetch. We start as soon as both are present — immediately if already
 * satisfied, else on the first MSAL login/token event and/or the client store
 * publishing a `current`. Idempotent via the guard in `startPostLoginServices`.
 */
function startPostLoginServicesWhenReady(): void {
  const isReady = (): boolean =>
    Boolean(msalInstance.getActiveAccount()) && Boolean(useClientStore.getState().current);

  if (isReady()) {
    startPostLoginServices();
    return;
  }

  let msalCallbackId: string | null = null;
  let unsubscribeClient: (() => void) | null = null;

  const attempt = (): void => {
    if (!isReady()) return;
    startPostLoginServices();
    if (msalCallbackId) msalInstance.removeEventCallback?.(msalCallbackId);
    unsubscribeClient?.();
  };

  unsubscribeClient = useClientStore.subscribe(attempt);
  msalCallbackId =
    msalInstance.addEventCallback?.((evt) => {
      if (
        evt.eventType === 'msal:loginSuccess' ||
        evt.eventType === 'msal:ssoSilentSuccess' ||
        evt.eventType === 'msal:acquireTokenSuccess'
      ) {
        attempt();
      }
    }) ?? null;
}

void bootstrap();
